import { MutationCache, QueryCache } from '@tanstack/query-core';
import { QueryClient, defaultShouldDehydrateQuery, isServer } from '@tanstack/react-query';
import { trackError } from '@/utility/raygun';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
      dehydrate: {
        // include pending queries in dehydration
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) || query.state.status === 'pending',
        shouldRedactErrors: () => false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        const errorContext: Record<string, unknown> = {
          queryKey: query.queryKey,
          type: 'QUERY',
          errorUpdateCount: query.state.errorUpdateCount,
          status: query.state.status,
          dataUpdateCount: query.state.dataUpdateCount,
          fetchStatus: query.state.fetchStatus,
          backgroundFetch: query.state.dataUpdatedAt > 0,
        };

        trackError(error, errorContext);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, __, mutation) => {
        trackError(error, {
          mutationKey: mutation.options?.mutationKey,
          variables: variables,
          type: 'MUTATION',
        });
      },
    }),
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}
